import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Car,
  ArrowRight,
  ArrowLeft,
  Gauge,
  DollarSign,
  AlertCircle,
  Check,
  Loader,
  ExternalLink,
  MapPin,
  Hash,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { formatCurrency } from "../../lib/formatUtils"; // Assuming we have a currency formatting utility
import { MOCK_US_STATES } from "../PostAuthenticationSurvey";
const mapStateToAbbreviation = (stateName) => {
  if (!stateName) {
    return "";
  }
  // First check for exact match of full state name
  const matchedState = MOCK_US_STATES.find(
    (state) => state.text.toLowerCase() === stateName.toLowerCase()
  );

  if (matchedState) {
    return matchedState.value;
  }

  // If no match, return the original input (in case it's already an abbreviation)
  return stateName;
};

const RefinancingVehicleCompletion = ({
  selectedVehicle,
  onComplete,
  onBack,
}) => {
  const { user } = useAuth();

  // Determine what information we need to collect
  const hasVIN = selectedVehicle?.vin && selectedVehicle.vin.length > 5;
  const hasLicenseAndState = selectedVehicle?.plate && selectedVehicle?.state;

  const [formData, setFormData] = useState(() => {
    // First check sessionStorage for cached refinancing data
    const cachedData = sessionStorage.getItem("refinancingVehicleData");

    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        var data = {
          vin: parsed.vin || selectedVehicle?.vin || "",
          license: parsed.license || selectedVehicle?.plate || "",
          state:
            parsed.state ||
            mapStateToAbbreviation(selectedVehicle?.state) ||
            "",
          mileage: parsed.mileage || selectedVehicle?.mileage || "",
          loanBalance: parsed.loanBalance || "",
          monthlyPayment: parsed.monthlyPayment || "",
          year: selectedVehicle?.year || "",
          make: selectedVehicle?.make || "",
          model: selectedVehicle?.model || "",
        };
        console.log(data);
        return data;
      } catch (e) {
        console.error("Error parsing cached refinancing data:", e);
      }
    }
    // Fallback to selectedVehicle data
    return {
      vin: selectedVehicle?.vin || "",
      license: selectedVehicle?.plate || "",
      state: mapStateToAbbreviation(selectedVehicle?.state) || "",
      mileage: selectedVehicle?.mileage || "",
      loanBalance: "",
      monthlyPayment: "",
      year: selectedVehicle?.year || "",
      make: selectedVehicle?.make || "",
      model: selectedVehicle?.model || "",
    };
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Save form data to sessionStorage whenever it changes
  useEffect(() => {
    if (Object.values(formData).some((val) => val !== "" && val !== null)) {
      sessionStorage.setItem(
        "refinancingVehicleData",
        JSON.stringify(formData)
      );
    }
  }, [formData]);

  useEffect(() => {
    let mileage = selectedVehicle?.mileage || "";
    const cachedData = sessionStorage.getItem("refinancingVehicleData");
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        mileage = parsed.mileage || selectedVehicle?.mileage || "";
      } catch (e) {}
    }
    // Reset form data when selectedVehicle changes
    setFormData({
      vin: selectedVehicle?.vin || "",
      license: selectedVehicle?.plate || "",
      state: mapStateToAbbreviation(selectedVehicle?.state) || "",
      mileage: mileage,
      loanBalance: "",
      monthlyPayment: "",
      year: selectedVehicle?.year || "",
      make: selectedVehicle?.make || "",
      model: selectedVehicle?.model || "",
    });
  }, [selectedVehicle]);

  const handleInputChange = (field, value) => {
    // For currency fields, remove non-numeric characters
    if (field === "loanBalance" || field === "monthlyPayment") {
      // Remove dollar sign, commas, and non-numeric characters
      const numericValue = value.replace(/[^\d.]/g, "");
      setFormData((prev) => ({
        ...prev,
        [field]: numericValue,
      }));
    } else if (field === "vin" || field === "license") {
      // Uppercase for VIN and license plate
      setFormData((prev) => ({
        ...prev,
        [field]: value.toUpperCase(),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  // Validation rules based on what information we have
  const getRequiredFields = () => {
    if (hasVIN) {
      // If we have VIN, we need mileage, loan balance, and monthly payment
      return {
        vin: "VIN is required",
        mileage: "Mileage is required",
      };
      // loanBalance: "Loan balance is required",
      // monthlyPayment: "Monthly payment is required",
    } else if (hasLicenseAndState) {
      // If we have license + state, we need more details
      return {
        state: "State is required",
        license: "License plate is required",
        mileage: "Mileage is required",
      };
      // loanBalance: "Loan balance is required",
      // monthlyPayment: "Monthly payment is required",
    } else {
      // Fallback - need everything
      return {
        state: "State is required",
        mileage: "Mileage is required",
      };
      // loanBalance: "Loan balance is required",
      // monthlyPayment: "Monthly payment is required",
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = getRequiredFields();

    // Check required fields
    Object.entries(requiredFields).forEach(([field, errorMessage]) => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        newErrors[field] = errorMessage;
      }
    });
    //Validate mileage
    if (
      !formData.mileage ||
      isNaN(formData.mileage) ||
      parseInt(formData.mileage) < 0
    ) {
      newErrors.mileage = "Please enter a valid mileage";
    }

    // Validate VIN if present
    if (formData.vin && formData.vin.length > 0 && formData.vin.length !== 17) {
      newErrors.vin = "VIN must be exactly 17 characters";
    }

    // Validate loan balance
    // if (
    //   !formData.loanBalance ||
    //   isNaN(formData.loanBalance) ||
    //   parseFloat(formData.loanBalance) <= 0
    // ) {
    //   newErrors.loanBalance = "Please enter a valid loan balance";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Store the completed vehicle data
      const completeRefinancingData = {
        ...formData,
        // Convert string values to numbers
        mileage: parseInt(formData.mileage),
        completedAt: new Date().toISOString(),
        originalVehicle: selectedVehicle,
      };
      // loanBalance: parseFloat(formData.loanBalance),
      // monthlyPayment: parseFloat(formData.monthlyPayment),

      sessionStorage.setItem(
        "completeRefinancingData",
        JSON.stringify(completeRefinancingData)
      );

      // Call onComplete with the finalized data
      if (onComplete) {
        onComplete(completeRefinancingData);
      }
    } catch (err) {
      console.error("Error completing refinancing information:", err);
      setErrors({
        general: "Failed to process refinancing information. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
          <Car className="w-8 h-8 text-blue-500" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          Refinancing Details
        </h1>
        <p className="text-gray-600">
          {(() => {
            if (hasVIN) {
              return "We have your VIN! We just need to confirm your refinancing details.";
            } else if (hasLicenseAndState) {
              return "We have your license plate and state. Please provide your refinancing information.";
            } else {
              return "Please provide your complete vehicle and refinancing information.";
            }
          })()}
        </p>
      </div>

      {/* Selected Vehicle Summary */}
      <Card className="bg-blue-50 border-blue-100">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-blue-500 mt-1" />
            <div>
              <p className="font-medium text-blue-900">Selected Vehicle</p>
              <p className="text-sm text-blue-700">
                {selectedVehicle?.year} {selectedVehicle?.make}{" "}
                {selectedVehicle?.model}
                {selectedVehicle?.plate && ` • ${selectedVehicle.plate}`}
                {selectedVehicle?.state && ` • ${selectedVehicle.state}`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Vehicle and Loan Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* VIN Field - show if we have VIN */}
          {hasVIN && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                VIN{" "}
                {getRequiredFields().vin ? (
                  <span className="text-red-500">*</span>
                ) : (
                  ""
                )}
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.vin}
                  onChange={(e) => handleInputChange("vin", e.target.value)}
                  className={`w-full pl-10 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.vin ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter 17-character VIN"
                  maxLength={17}
                  readOnly={hasVIN}
                  disabled={hasVIN}
                />
              </div>
              {errors.vin && (
                <p className="text-red-500 text-sm">{errors.vin}</p>
              )}
            </div>
          )}

          {/* License Plate - show if we don't have VIN */}
          {!hasVIN && (
            <>
              {/* License Plate */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  License Plate{" "}
                  {getRequiredFields().license ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    ""
                  )}
                </label>
                <input
                  type="text"
                  value={formData.license}
                  onChange={(e) => handleInputChange("license", e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.license ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter license plate"
                />
                {errors.license && (
                  <p className="text-red-500 text-sm">{errors.license}</p>
                )}
              </div>

              {/* State */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  State{" "}
                  {getRequiredFields().state ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    ""
                  )}
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <select
                    value={formData.state}
                    onChange={(e) => handleInputChange("state", e.target.value)}
                    className={`w-full pl-10 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none ${
                      errors.state ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select state...</option>
                    {MOCK_US_STATES.map((state) => (
                      <option key={state.text} value={state.value}>
                        {state.text}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.state && (
                  <p className="text-red-500 text-sm">{errors.state}</p>
                )}
              </div>
            </>
          )}

          {/* Mileage Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Current Mileage <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Gauge className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={formData.mileage}
                onChange={(e) => handleInputChange("mileage", e.target.value)}
                className={`w-full pl-10 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.mileage ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter current vehicle mileage"
              />
            </div>
            {errors.mileage && (
              <p className="text-red-500 text-sm">{errors.mileage}</p>
            )}
          </div>

          {/* Estimated Loan Balance */}
          {/* <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Estimated Loan Balance <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={
                  formData.loanBalance
                    ? formatCurrency(formData.loanBalance)
                    : ""
                }
                onChange={(e) =>
                  handleInputChange("loanBalance", e.target.value)
                }
                className={`w-full pl-10 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.loanBalance ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter estimated loan balance"
              />
            </div>
            {errors.loanBalance && (
              <p className="text-red-500 text-sm">{errors.loanBalance}</p>
            )}
          </div> */}

          {/* Estimated Monthly Payment */}
          {/* <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Current Monthly Payment <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={
                  formData.monthlyPayment
                    ? formatCurrency(formData.monthlyPayment)
                    : ""
                }
                onChange={(e) =>
                  handleInputChange("monthlyPayment", e.target.value)
                }
                className={`w-full pl-10 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.monthlyPayment ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter current monthly payment"
              />
            </div>
            {errors.monthlyPayment && (
              <p className="text-red-500 text-sm">{errors.monthlyPayment}</p>
            )}
          </div> */}
        </CardContent>
      </Card>

      {/* Next Steps Info */}
      <Card className="bg-green-50 border-green-100">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <ExternalLink className="w-5 h-5 text-green-500 mt-1" />
            <div>
              <p className="font-medium text-green-900">
                Next: Refinancing Options
              </p>
              <p className="text-sm text-green-700">
                Once you complete this information, we'll connect you with the
                best refinancing options for your vehicle.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {errors.general && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{errors.general}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors font-medium ${
            !isSubmitting
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {isSubmitting ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>

        {onBack && (
          <button
            onClick={onBack}
            className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Vehicle Selection</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default RefinancingVehicleCompletion;
